import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowsClockwise, Target, Shuffle, User, Warning, ClipboardText, Package,
  CheckCircle, XCircle, CalendarBlank, Clock, ArrowUp, ArrowDown, Info,
  DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";

const todayISO = () => new Date().toISOString().slice(0, 10);
const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};
const fmtKg = (n) => `${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 3 })} kg`;
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "-";
const fmtDateLong = (d) => new Date(d + "T12:00:00").toLocaleDateString("es-AR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

function ItemRow({ item, fecha, onChecked, testidPrefix, idx }) {
  const [openDif, setOpenDif] = useState(false);
  const [peso, setPeso] = useState(String(item.kg_producidos ?? ""));
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [refactState, setRefactState] = useState(null); // { factura_numero, factura_id, pedido_id, puede_nc }
  const [refactWorking, setRefactWorking] = useState(false);
  const navigate = useNavigate();
  const already = item.check_estado;

  // Combos: se auditan componente por componente (un peso real por producto).
  const esCombo = !!(item.es_combo && Array.isArray(item.combo_componentes) && item.combo_componentes.length > 0);
  const comps = esCombo ? item.combo_componentes : [];
  const [compPesos, setCompPesos] = useState(() =>
    Object.fromEntries(comps.map((c) => [c.componente_idx, String(c.kg_producidos ?? "")]))
  );

  const basePayload = () => ({
    source_type: item.source_type, source_id: item.source_id, item_idx: item.item_idx,
    fecha_envio: fecha,
  });

  const markOk = async () => {
    setSaving(true);
    try {
      await api.post("/auditoria-salida/check", {
        ...basePayload(),
        estado: "ok",
        // En combos el OK registra cada componente con su peso producido.
        ...(esCombo
          ? { componentes: comps.map((c) => ({ componente_idx: c.componente_idx, peso_real: Number(c.kg_producidos ?? 0) })) }
          : {}),
      });
      toast.success("Marcado OK");
      onChecked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const revertir = async () => {
    if (!item.check_id) return;
    if (!window.confirm(
      `¿Revertir este control?${
        item.diferencia && Math.abs(item.diferencia) > 0.001
          ? `\n\nAtención: se aplicará el ajuste inverso al stock (${item.diferencia > 0 ? "-" : "+"}${Math.abs(item.diferencia).toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg).`
          : ""
      }`
    )) return;
    setReverting(true);
    try {
      const { data } = await api.delete(`/auditoria-salida/check/${encodeURIComponent(item.check_id)}`);
      const ns = data?.nuevo_stock;
      toast.success(`Control revertido${ns != null ? ` · Stock ahora: ${fmtKg(ns)}` : ""}`, { duration: 5000 });
      onChecked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revertir");
    } finally { setReverting(false); }
  };

  const saveDif = async () => {
    let payload;
    let hasDif = false;
    if (esCombo) {
      const componentes = [];
      for (const c of comps) {
        const p = parseFloat(compPesos[c.componente_idx]);
        if (!Number.isFinite(p) || p < 0) {
          return toast.warning(`Peso real inválido para ${c.producto}`);
        }
        if (Math.abs(Number(c.kg_producidos ?? 0) - p) > 0.001) hasDif = true;
        componentes.push({ componente_idx: c.componente_idx, peso_real: p, observaciones: obs || null });
      }
      payload = { ...basePayload(), estado: hasDif ? "con_diferencia" : "ok", componentes };
    } else {
      const pesoNum = parseFloat(peso);
      if (!Number.isFinite(pesoNum) || pesoNum < 0) return toast.warning("Peso real inválido");
      hasDif = Math.abs(Number(item.kg_producidos) - pesoNum) > 0.001;
      payload = { ...basePayload(), estado: "con_diferencia", peso_real: pesoNum, observaciones: obs || null };
    }
    setSaving(true);
    try {
      const { data } = await api.post("/auditoria-salida/check", payload);
      const dif = data?.check?.diferencia || 0;
      const ns = data?.nuevo_stock;
      const cid = data?.check?.id;
      if (hasDif) {
        toast.success(
          esCombo
            ? "Guardado. Stock ajustado por componente según las diferencias."
            : `Guardado. Stock ajustado: ${dif > 0 ? "+" : ""}${dif.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg${ns != null ? ` · Total ahora: ${fmtKg(ns)}` : ""}`,
          { duration: 6000 }
        );
        // Post-check: si el pedido ya está facturado (sólo pedidos manuales),
        // ofrecer el flujo NC + Refacturar.
        if (cid && item.source_type === "pedido") {
          try {
            const { data: est } = await api.get(`/auditoria-salida/estado-facturacion/${encodeURIComponent(cid)}`);
            if (est?.aplicable && est?.ya_facturado) {
              setRefactState(est);
            }
          } catch (_) { /* silencioso */ }
        }
      } else {
        toast.success("Guardado sin diferencia significativa");
      }
      setOpenDif(false); setObs("");
      onChecked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const refacturar = async () => {
    if (!refactState?.puede_nc || !refactState?.factura_id) {
      return toast.error(refactState?.motivo || "No se puede generar NC automáticamente");
    }
    if (!window.confirm(
      `Se emitirá una Nota de Crédito para la factura ${refactState.factura_tipo} ${refactState.factura_numero} (total ${refactState.factura_total}) ` +
      `y luego serás redirigido a facturar el pedido con los kg auditados. ¿Continuar?`
    )) return;
    setRefactWorking(true);
    try {
      await api.post(`/facturas/${refactState.factura_id}/nota-credito`, {
        auto_afip: true,
        motivo: `Refacturación por ajuste de Auditoría de Salida (control ${refactState.factura_numero})`,
      });
      toast.success("NC emitida correctamente · redirigiendo a facturar…", { duration: 4000 });
      setTimeout(() => {
        navigate(`/ventas?pedido_id=${refactState.pedido_id}`);
      }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo emitir la NC");
    } finally { setRefactWorking(false); }
  };

  const diff = Number(item.kg_producidos) - Number(item.kg_esperados);
  // Cuando el ítem ya fue controlado lo mostramos "apagado" (opacidad + fondo gris)
  // y con un botón Revertir prominente.
  const bgCls = already === "ok"
    ? "bg-neutral-100/70 opacity-70"
    : already === "con_diferencia"
      ? "bg-amber-50/70 opacity-90"
      : "";

  return (
    <>
      <tr className={`border-t border-neutral-100 hover:bg-neutral-50 ${bgCls}`} data-testid={`${testidPrefix}-row-${idx}`}>
        <td className="px-3 py-1.5 font-mono font-bold text-[#6B1F24]">{item.numero_pedido}</td>
        <td className="px-3 py-1.5">
          <User size={11} className="inline text-neutral-400 mr-1" />
          {item.cliente_nombre || <span className="text-neutral-400 italic">—</span>}
        </td>
        <td className="px-3 py-1.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            {item.rubro_color && (<span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: item.rubro_color }} />)}
            <span className={already ? "line-through decoration-neutral-400 decoration-1" : ""}>{item.producto}</span>
            {esCombo && (
              <span className="inline-block text-[9px] font-mono font-bold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm"
                data-testid={`${testidPrefix}-combo-badge-${idx}`}>
                COMBO · {comps.length} prod.
              </span>
            )}
          </div>
          {item.especificacion && (<div className="text-[11px] italic text-amber-700 ml-3">↳ {item.especificacion}</div>)}
          {esCombo && (
            <div className="ml-3 mt-1 space-y-0.5" data-testid={`${testidPrefix}-combo-desglose-${idx}`}>
              {comps.map((c) => {
                const difC = Number(c.kg_producidos ?? 0) - Number(c.kg_esperados ?? 0);
                return (
                  <div key={c.componente_idx} className="text-[11px] font-mono text-neutral-600 flex flex-wrap gap-x-2">
                    <span className="uppercase">↳ {c.producto}</span>
                    <span className="text-neutral-400">esp {fmtKg(c.kg_esperados)}</span>
                    <span className={Math.abs(difC) > 0.001 ? "text-amber-700 font-bold" : "text-neutral-500"}>
                      prod {fmtKg(c.kg_producidos ?? 0)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </td>
        <td className="px-3 py-1.5 text-right font-mono text-neutral-600">{fmtKg(item.kg_esperados)}</td>
        <td className="px-3 py-1.5 text-right font-mono font-bold">
          {fmtKg(item.kg_producidos)}
          {Math.abs(diff) > 0.001 && (
            <span className={`ml-1 text-[10px] ${diff < 0 ? "text-rose-600" : "text-emerald-600"}`}>
              ({diff >= 0 ? "+" : ""}{diff.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })})
            </span>
          )}
        </td>
        <td className="px-3 py-1.5">
          {item.producido_por ? (
            <span className="inline-flex items-center gap-1 bg-[#F5F1E9] text-[#6B1F24] px-2 py-0.5 rounded-sm font-mono text-[10px]">
              <User size={9} weight="bold" /> {item.producido_por}
              {item.producido_at && <span className="text-neutral-500 ml-1">{fmtTime(item.producido_at)}</span>}
            </span>
          ) : <span className="text-neutral-400 italic text-[10px]">sin marcar</span>}
        </td>
        <td className="px-3 py-1.5 text-right">
          {already ? (
            <div className="flex flex-col items-end gap-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono ${
                already === "ok" ? "bg-neutral-200 text-neutral-700" : "bg-amber-200 text-amber-900"
              }`}>
                {already === "ok" ? <><CheckCircle size={10} weight="fill" /> OK</> : <><Warning size={10} weight="fill" /> Con dif.</>}
              </span>
              {already === "con_diferencia" && item.peso_real != null && !esCombo && (
                <span className="text-[10px] font-mono text-neutral-600">
                  Real: {fmtKg(item.peso_real)}
                  {item.diferencia != null && Math.abs(item.diferencia) > 0.001 && (
                    <span className={item.diferencia > 0 ? "text-emerald-700 ml-1" : "text-rose-700 ml-1"}>
                      ({item.diferencia > 0 ? "+" : ""}{item.diferencia.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })})
                    </span>
                  )}
                </span>
              )}
              {esCombo && Array.isArray(item.check_componentes) && (
                <div className="text-[10px] font-mono text-neutral-600 space-y-0.5 text-right"
                  data-testid={`${testidPrefix}-combo-check-${idx}`}>
                  {item.check_componentes.map((cc) => (
                    <div key={cc.componente_idx}>
                      <span className="uppercase">{cc.producto}</span>: real {fmtKg(cc.peso_real)}
                      {cc.diferencia != null && Math.abs(cc.diferencia) > 0.001 && (
                        <span className={cc.diferencia > 0 ? "text-emerald-700 ml-1" : "text-rose-700 ml-1"}>
                          ({cc.diferencia > 0 ? "+" : ""}{cc.diferencia.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {item.check_by && (
                <span className="text-[9px] text-neutral-500 font-mono">
                  por {item.check_by} {item.check_at && fmtTime(item.check_at)}
                </span>
              )}
              <Button
                size="sm" variant="outline" onClick={revertir} disabled={reverting}
                className="h-7 border-rose-400 text-rose-700 hover:bg-rose-50 rounded-sm text-[11px] mt-0.5"
                data-testid={`${testidPrefix}-revertir-${idx}`}
              >
                <XCircle size={11} weight="bold" className="mr-1" />
                {reverting ? "Revirtiendo…" : "Revertir"}
              </Button>
            </div>
          ) : (
            <div className="flex gap-1 justify-end">
              <Button size="sm" onClick={markOk} disabled={saving}
                className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] rounded-sm"
                data-testid={`${testidPrefix}-ok-${idx}`}>
                <CheckCircle size={11} weight="fill" className="mr-1" /> OK
              </Button>
              <Button size="sm" variant="outline" onClick={() => setOpenDif((v) => !v)}
                disabled={saving}
                className="h-7 border-amber-500 text-amber-800 hover:bg-amber-50 text-[11px] rounded-sm"
                data-testid={`${testidPrefix}-dif-${idx}`}>
                <Warning size={11} className="mr-1" /> Con dif.
              </Button>
            </div>
          )}
        </td>
      </tr>
      {openDif && !esCombo && (
        <tr className="bg-amber-50/40 border-t border-amber-100" data-testid={`${testidPrefix}-dif-form-${idx}`}>
          <td colSpan={7} className="px-3 py-2">
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-[10px] uppercase font-mono text-neutral-600">Peso real (kg)</Label>
                <NumberInput step="0.001" min="0" value={peso}
                  onChange={(e) => setPeso(e.target.value)}
                  className="h-8 w-28 mt-0.5 font-mono text-right"
                  data-testid={`${testidPrefix}-peso-input-${idx}`}
                  autoFocus
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-[10px] uppercase font-mono text-neutral-600">Observaciones (opcional)</Label>
                <Input value={obs} onChange={(e) => setObs(e.target.value)}
                  placeholder="Motivo, comentario..." className="h-8 mt-0.5"
                  data-testid={`${testidPrefix}-obs-input-${idx}`} />
              </div>
              <div className="text-[11px] font-mono text-neutral-600 pb-1">
                {peso && Number.isFinite(parseFloat(peso)) && (
                  (() => {
                    const dif = Number(item.kg_producidos) - parseFloat(peso);
                    if (Math.abs(dif) < 0.001) return <span className="text-neutral-500">Sin ajuste</span>;
                    return dif > 0 ? (
                      <span className="text-emerald-700 font-bold">
                        <ArrowUp size={11} className="inline" /> +{dif.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg al stock
                      </span>
                    ) : (
                      <span className="text-rose-700 font-bold">
                        <ArrowDown size={11} className="inline" /> {dif.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg del stock
                      </span>
                    );
                  })()
                )}
              </div>
              <Button size="sm" onClick={saveDif} disabled={saving}
                className="h-8 bg-[#6B1F24] hover:bg-[#571519] text-white rounded-sm"
                data-testid={`${testidPrefix}-save-${idx}`}>
                {saving ? "Guardando…" : "Guardar y ajustar stock"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setOpenDif(false)} className="h-8">
                Cancelar
              </Button>
            </div>
          </td>
        </tr>
      )}
      {openDif && esCombo && (
        <tr className="bg-amber-50/40 border-t border-amber-100" data-testid={`${testidPrefix}-dif-form-${idx}`}>
          <td colSpan={7} className="px-3 py-2">
            <div className="space-y-2">
              {comps.map((c) => {
                const raw = compPesos[c.componente_idx] ?? "";
                const p = parseFloat(raw);
                const difC = Number.isFinite(p) ? Number(c.kg_producidos ?? 0) - p : 0;
                return (
                  <div key={c.componente_idx} className="flex flex-wrap items-end gap-2"
                    data-testid={`${testidPrefix}-dif-comp-${idx}-${c.componente_idx}`}>
                    <div className="w-44">
                      <div className="text-[11px] font-semibold uppercase">{c.producto}</div>
                      <div className="text-[10px] font-mono text-neutral-400">producido {fmtKg(c.kg_producidos ?? 0)}</div>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase font-mono text-neutral-600">Peso real (kg)</Label>
                      <NumberInput step="0.001" min="0" value={raw}
                        onChange={(e) => setCompPesos((cur) => ({ ...cur, [c.componente_idx]: e.target.value }))}
                        className="h-8 w-28 mt-0.5 font-mono text-right"
                        data-testid={`${testidPrefix}-peso-comp-${idx}-${c.componente_idx}`}
                      />
                    </div>
                    <div className="text-[11px] font-mono text-neutral-600 pb-1">
                      {Number.isFinite(p) && (
                        Math.abs(difC) < 0.001 ? <span className="text-neutral-500">Sin ajuste</span>
                          : difC > 0 ? (
                            <span className="text-emerald-700 font-bold">
                              <ArrowUp size={11} className="inline" /> +{difC.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg al stock
                            </span>
                          ) : (
                            <span className="text-rose-700 font-bold">
                              <ArrowDown size={11} className="inline" /> {difC.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg del stock
                            </span>
                          )
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-amber-200">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-[10px] uppercase font-mono text-neutral-600">Observaciones (opcional)</Label>
                  <Input value={obs} onChange={(e) => setObs(e.target.value)}
                    placeholder="Motivo, comentario..." className="h-8 mt-0.5"
                    data-testid={`${testidPrefix}-obs-input-${idx}`} />
                </div>
                <Button size="sm" onClick={saveDif} disabled={saving}
                  className="h-8 bg-[#6B1F24] hover:bg-[#571519] text-white rounded-sm"
                  data-testid={`${testidPrefix}-save-${idx}`}>
                  {saving ? "Guardando…" : "Guardar y ajustar stock por componente"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setOpenDif(false)} className="h-8">
                  Cancelar
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}
      {refactState && (
        <tr className="bg-sky-50 border-t border-sky-200" data-testid={`${testidPrefix}-refact-banner-${idx}`}>
          <td colSpan={7} className="px-3 py-2">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[300px]">
                <div className="text-[12px] font-semibold text-sky-900 flex items-center gap-1.5">
                  <Warning size={13} weight="fill" className="text-sky-700" />
                  Este pedido ya fue facturado con CAE
                </div>
                <div className="text-[11px] text-sky-800 mt-0.5 font-mono">
                  Factura {refactState.factura_tipo} {refactState.factura_numero} · Total {refactState.factura_total}
                </div>
                <div className="text-[11px] text-sky-700 mt-1">
                  {refactState.motivo}
                </div>
              </div>
              <Button
                size="sm"
                onClick={refacturar}
                disabled={refactWorking || !refactState.puede_nc}
                className="h-8 bg-sky-700 hover:bg-sky-800 text-white rounded-sm"
                data-testid={`${testidPrefix}-refacturar-${idx}`}
              >
                {refactWorking ? "Emitiendo NC…" : "Emitir NC + Refacturar"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRefactState(null)}
                className="h-8 text-sky-700"
                data-testid={`${testidPrefix}-refact-dismiss-${idx}`}
              >
                Cerrar
              </Button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function AudTable({ items, testidPrefix, emptyText, tone, fecha, onChecked }) {
  const toneCls = tone === "warn" ? "border-amber-300 bg-amber-50/40"
    : tone === "info" ? "border-sky-300 bg-sky-50/40"
    : "border-neutral-200 bg-white";
  if (!items || items.length === 0) {
    return (
      <div className={`border rounded-sm p-6 text-center text-neutral-500 text-[13px] ${toneCls}`}>{emptyText}</div>
    );
  }
  return (
    <div className={`border rounded-sm overflow-hidden ${toneCls}`}>
      <table className="w-full text-[12px]">
        <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-[#666]">
          <tr>
            <th className="px-3 py-1.5 text-left w-28">Nº Pedido</th>
            <th className="px-3 py-1.5 text-left">Cliente</th>
            <th className="px-3 py-1.5 text-left">Producto</th>
            <th className="px-3 py-1.5 text-right w-24">Kg esp.</th>
            <th className="px-3 py-1.5 text-right w-28">Kg prod.</th>
            <th className="px-3 py-1.5 text-left w-40">Producido por</th>
            <th className="px-3 py-1.5 text-right w-52">Control</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <ItemRow key={`${it.source_id}-${it.item_idx}-${i}`} item={it} idx={i}
              fecha={fecha} testidPrefix={testidPrefix} onChecked={onChecked} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AuditoriaSalidaPage() {
  const [tab, setTab] = useState("hoy");
  const [fecha, setFecha] = useState(todayISO());
  const [muestraPct, setMuestraPct] = useState(5);
  const [muestraPctLoaded, setMuestraPctLoaded] = useState(false);
  const [data, setData] = useState({ exactos: [], aleatorios: [], total_candidatos: 0, total_exactos: 0, total_aleatorios: 0, total_restantes: 0 });
  const [loading, setLoading] = useState(false);

  // Carga inicial del % configurado (global, compartido entre usuarios).
  useEffect(() => {
    api.get("/auditoria-salida/settings")
      .then(({ data }) => setMuestraPct(Number(data?.muestra_pct) || 5))
      .catch(() => {})
      .finally(() => setMuestraPctLoaded(true));
  }, []);

  // Persistencia con debounce cuando el usuario cambia el %.
  // No dispara hasta que la carga inicial terminó (evita pisar el valor).
  useEffect(() => {
    if (!muestraPctLoaded) return;
    const t = setTimeout(() => {
      api.put("/auditoria-salida/settings", { muestra_pct: muestraPct }).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [muestraPct, muestraPctLoaded]);

  const load = useCallback(async () => {
    if (!muestraPctLoaded) return; // esperar a tener el % correcto antes de pegarle a la API
    setLoading(true);
    try {
      const { data } = await api.get("/auditoria-salida", {
        params: { fecha, muestra_pct: muestraPct, incluir_pickup: "true" },
      });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la auditoría");
    } finally { setLoading(false); }
  }, [fecha, muestraPct, muestraPctLoaded]);
  useEffect(() => { if (tab === "hoy") load(); }, [load, tab]);

  // Histórico
  const [histDesde, setHistDesde] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [histHasta, setHistHasta] = useState(todayISO());
  const [histEstado, setHistEstado] = useState("todos");
  const [histUsuario, setHistUsuario] = useState("todos");
  const [historial, setHistorial] = useState({ grupos: [], usuarios_disponibles: [], total: 0 });
  const [histLoading, setHistLoading] = useState(false);

  const loadHistorial = useCallback(async () => {
    setHistLoading(true);
    try {
      const params = { desde: histDesde, hasta: histHasta };
      if (histEstado !== "todos") params.estado = histEstado;
      if (histUsuario !== "todos") params.usuario = histUsuario;
      const { data } = await api.get("/auditoria-salida/historial", { params });
      setHistorial(data);
    } finally { setHistLoading(false); }
  }, [histDesde, histHasta, histEstado, histUsuario]);

  const exportarHistorial = useCallback(async () => {
    try {
      const params = { desde: histDesde, hasta: histHasta };
      if (histEstado !== "todos") params.estado = histEstado;
      if (histUsuario !== "todos") params.usuario = histUsuario;
      const resp = await api.get("/auditoria-salida/historial/export", {
        params, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `auditoria_salida_${histDesde}_${histHasta}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo exportar");
    }
  }, [histDesde, histHasta, histEstado, histUsuario]);
  useEffect(() => { if (tab === "historial") loadHistorial(); }, [tab, loadHistorial]);

  return (
    <>
      <Topbar
        title="Auditoría de Salida"
        subtitle="Controles de calidad sobre kilos producidos: números exactos + muestra aleatoria"
      />

      <div className="p-4">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="rounded-sm">
            <TabsTrigger value="hoy" data-testid="aud-tab-hoy" className="rounded-sm">Del día</TabsTrigger>
            <TabsTrigger value="historial" data-testid="aud-tab-historial" className="rounded-sm">Histórico</TabsTrigger>
          </TabsList>

          <TabsContent value="hoy" className="space-y-5 mt-3">
            {/* Filtros */}
            <div className="bg-white border border-[#E5E5E5] rounded-sm p-3 flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500">Fecha de envío</Label>
                <div className="flex gap-1.5 mt-1" data-testid="aud-salida-fecha-toggle">
                  <button
                    type="button"
                    onClick={() => setFecha(todayISO())}
                    className={`px-4 h-9 rounded-sm font-mono text-[12px] uppercase tracking-wide transition-colors ${
                      fecha === todayISO()
                        ? "bg-[#6B1F24] text-white border border-[#6B1F24]"
                        : "bg-white text-neutral-700 border border-neutral-300 hover:bg-neutral-50"
                    }`}
                    data-testid="aud-salida-fecha-hoy"
                  >
                    Hoy
                  </button>
                  <button
                    type="button"
                    onClick={() => setFecha(tomorrowISO())}
                    className={`px-4 h-9 rounded-sm font-mono text-[12px] uppercase tracking-wide transition-colors ${
                      fecha === tomorrowISO()
                        ? "bg-[#6B1F24] text-white border border-[#6B1F24]"
                        : "bg-white text-neutral-700 border border-neutral-300 hover:bg-neutral-50"
                    }`}
                    data-testid="aud-salida-fecha-manana"
                  >
                    Mañana
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500">Muestra %</Label>
                <NumberInput min="0.5" max="100" step="0.5" value={muestraPct}
                  onChange={(e) => setMuestraPct(Math.max(0.5, Math.min(100, Number(e.target.value) || 5)))}
                  className="rounded-sm h-9 mt-1 w-24 font-mono" data-testid="aud-salida-muestra" />
              </div>
              <div className="ml-auto flex gap-2 items-center flex-wrap" />
            </div>

            {loading && <div className="text-center py-10 text-neutral-500">Cargando…</div>}

            {!loading && (
              <>
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <Target size={18} weight="bold" className="text-amber-700" />
                    <h2 className="font-heading font-semibold text-[15px]">Auditoría por Números Exactos</h2>
                  </div>
                  <AudTable items={data.exactos} testidPrefix="aud-exactos" tone="warn" fecha={fecha} onChecked={load}
                    emptyText={<><ClipboardText size={30} weight="duotone" className="mx-auto mb-1 text-amber-300" /> Ningún ítem con kg exactos al esperado — todos los operadores registraron pesos reales.</>} />
                </section>

                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <Shuffle size={18} weight="bold" className="text-sky-700" />
                    <h2 className="font-heading font-semibold text-[15px]">Auditoría Aleatoria</h2>
                  </div>
                  <AudTable items={data.aleatorios} testidPrefix="aud-aleatorios" tone="info" fecha={fecha} onChecked={load}
                    emptyText={<><Package size={30} weight="duotone" className="mx-auto mb-1 text-sky-300" /> No hay ítems para muestrear en esta fecha.</>} />
                </section>

                {data.total_candidatos === 0 && (
                  <div className="bg-white border border-[#E5E5E5] rounded-sm p-8 text-center text-neutral-500">
                    <Warning size={30} weight="duotone" className="mx-auto mb-2 text-neutral-300" />
                    No hay ítems producidos para esta fecha.
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="historial" className="space-y-4 mt-3">
            <div className="bg-white border border-[#E5E5E5] rounded-sm p-3 flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500">Desde</Label>
                <Input type="date" value={histDesde} onChange={(e) => setHistDesde(e.target.value)}
                  className="rounded-sm h-9 mt-1 w-40 font-mono" data-testid="aud-hist-desde" />
              </div>
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500">Hasta</Label>
                <Input type="date" value={histHasta} onChange={(e) => setHistHasta(e.target.value)}
                  className="rounded-sm h-9 mt-1 w-40 font-mono" data-testid="aud-hist-hasta" />
              </div>
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500">Estado</Label>
                <Select value={histEstado} onValueChange={setHistEstado}>
                  <SelectTrigger className="rounded-sm h-9 mt-1 w-44" data-testid="aud-hist-estado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="ok">OK</SelectItem>
                    <SelectItem value="con_diferencia">Con diferencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500">Usuario</Label>
                <Select value={histUsuario} onValueChange={setHistUsuario}>
                  <SelectTrigger className="rounded-sm h-9 mt-1 w-48" data-testid="aud-hist-usuario"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {historial.usuarios_disponibles.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={loadHistorial} disabled={histLoading} className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519]" data-testid="aud-hist-buscar">
                {histLoading ? "Buscando…" : "Buscar"}
              </Button>
              <div className="ml-auto border border-neutral-200 rounded-sm px-3 py-1.5 min-w-[130px]">
                <div className="text-[9px] uppercase font-mono text-neutral-500">Total controles</div>
                <div className="text-lg font-heading font-bold" data-testid="aud-hist-total">{historial.total}</div>
              </div>
              <Button
                variant="outline"
                onClick={exportarHistorial}
                disabled={histLoading || (historial.total || 0) === 0}
                className="rounded-sm h-9 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                data-testid="aud-hist-export"
              >
                <DownloadSimple size={13} className="mr-1" /> Exportar Excel
              </Button>
            </div>

            {histLoading && <div className="text-center py-10 text-neutral-500">Cargando…</div>}
            {!histLoading && historial.grupos.length === 0 && (
              <div className="bg-white border border-[#E5E5E5] rounded-sm p-10 text-center text-neutral-500">
                <ClipboardText size={38} weight="duotone" className="mx-auto mb-2 text-neutral-300" />
                No hay controles registrados en el rango seleccionado.
              </div>
            )}
            {!histLoading && historial.grupos.map((g) => (
              <div key={g.fecha_envio} className="bg-white border border-[#E5E5E5] rounded-sm overflow-hidden" data-testid={`aud-hist-grupo-${g.fecha_envio}`}>
                <div className="bg-neutral-50 border-b border-[#E5E5E5] px-4 py-2 flex items-center gap-3 flex-wrap">
                  <CalendarBlank size={16} className="text-[#6B1F24]" />
                  <div className="font-heading font-semibold text-[14px] capitalize">{fmtDateLong(g.fecha_envio)}</div>
                  <div className="text-[11px] font-mono text-neutral-500">
                    · {g.total} control{g.total !== 1 ? "es" : ""}
                    <span className="ml-2 text-emerald-700">OK: {g.ok}</span>
                    <span className="ml-2 text-amber-700">Con dif.: {g.con_diferencia}</span>
                    {g.kg_ajustados > 0 && <span className="ml-2 text-neutral-700">Ajustado: {fmtKg(g.kg_ajustados)}</span>}
                  </div>
                </div>
                <table className="w-full text-[12px]">
                  <thead className="bg-neutral-50/50 text-[10px] uppercase font-mono text-[#666]">
                    <tr>
                      <th className="px-3 py-1.5 text-left w-20">Hora</th>
                      <th className="px-3 py-1.5 text-left w-28">Nº Pedido</th>
                      <th className="px-3 py-1.5 text-left">Cliente</th>
                      <th className="px-3 py-1.5 text-left">Producto</th>
                      <th className="px-3 py-1.5 text-right w-24">Producido</th>
                      <th className="px-3 py-1.5 text-right w-24">Peso real</th>
                      <th className="px-3 py-1.5 text-right w-24">Ajuste</th>
                      <th className="px-3 py-1.5 text-left w-32">Estado</th>
                      <th className="px-3 py-1.5 text-left w-32">Controlado por</th>
                      <th className="px-3 py-1.5 text-left">Obs.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.id} className="border-t border-neutral-100" data-testid={`aud-hist-row-${r.id}`}>
                        <td className="px-3 py-1.5 font-mono text-neutral-600"><Clock size={11} className="inline mr-1 text-neutral-400" />{fmtTime(r.at)}</td>
                        <td className="px-3 py-1.5 font-mono font-bold text-[#6B1F24]">{r.numero_pedido}</td>
                        <td className="px-3 py-1.5">{r.cliente_nombre || <span className="text-neutral-400 italic">—</span>}</td>
                        <td className="px-3 py-1.5">{r.producto_nombre || "—"}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtKg(r.kg_producidos_snapshot)}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{r.peso_real != null ? fmtKg(r.peso_real) : "—"}</td>
                        <td className={`px-3 py-1.5 text-right font-mono font-bold ${r.diferencia > 0 ? "text-emerald-700" : r.diferencia < 0 ? "text-rose-700" : "text-neutral-500"}`}>
                          {r.diferencia && Math.abs(r.diferencia) > 0.001 ? `${r.diferencia > 0 ? "+" : ""}${r.diferencia.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg` : "—"}
                        </td>
                        <td className="px-3 py-1.5">
                          {r.estado === "ok" ? (
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-sm text-[10px] font-mono">
                              <CheckCircle size={10} weight="fill" /> OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-0.5 rounded-sm text-[10px] font-mono">
                              <Warning size={10} weight="fill" /> Con diferencia
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5">
                          <span className="inline-flex items-center gap-1 bg-[#F5F1E9] text-[#6B1F24] px-2 py-0.5 rounded-sm font-mono text-[10px]">
                            <User size={9} weight="bold" /> {r.by_username || "-"}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-[11px] italic text-neutral-500">{r.observaciones || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
